import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as billing from "../../../interfaces/screens/billing";
import { fetchInvoices, fetchClaims, fetchBillingCodes } from "./actions";

interface BillingState {
  invoices: {
    data: billing.Invoice[];
    loading: boolean;
    error: string | null;
  };
  billingCodes: {
    data: billing.BillingCode[];
    loading: boolean;
    error: string | null;
  };
  claims: {
    data: billing.Claim[];
    loading: boolean;
    error: string | null;
  };
}

const getInitialState = (): BillingState => {
  return {
    invoices: {
      data: [],
      loading: false,
      error: null,
    },
    billingCodes: {
      data: [],
      loading: false,
      error: null,
    },
    claims: {
      data: [],
      loading: false,
      error: null,
    },
  };
};

const billingSlice = createSlice({
  name: "billing",
  initialState: getInitialState(),
  reducers: {
    addBillingCode: (state, action: PayloadAction<billing.BillingCode>) => {
      state.billingCodes.data.unshift(action.payload);
    },
    updateBillingCode: (state, action: PayloadAction<billing.BillingCode>) => {
      const index = state.billingCodes.data.findIndex(
        (code) => code.id === action.payload.id,
      );
      if (index !== -1) {
        state.billingCodes.data[index] = action.payload;
      }
    },
    deleteBillingCode: (state, action: PayloadAction<string>) => {
      state.billingCodes.data = state.billingCodes.data.filter(
        (code) => code.id !== action.payload,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.invoices.loading = true;
        state.invoices.error = null;
      })
      .addCase(
        fetchInvoices.fulfilled,
        (state, action: PayloadAction<billing.Invoice[]>) => {
          state.invoices.loading = false;
          state.invoices.data = action.payload;
        },
      )
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.invoices.loading = false;
        state.invoices.error =
          action.error.message || "Failed to fetch invoices";
      })
      .addCase(fetchBillingCodes.pending, (state) => {
        state.billingCodes.loading = true;
        state.billingCodes.error = null;
      })
      .addCase(
        fetchBillingCodes.fulfilled,
        (state, action: PayloadAction<billing.BillingCode[]>) => {
          state.billingCodes.loading = false;
          state.billingCodes.data = action.payload;
        },
      )
      .addCase(fetchBillingCodes.rejected, (state, action) => {
        state.billingCodes.loading = false;
        state.billingCodes.error =
          action.error.message || "Failed to fetch billing codes";
      })
      .addCase(fetchClaims.pending, (state) => {
        state.claims.loading = true;
        state.claims.error = null;
      })
      .addCase(
        fetchClaims.fulfilled,
        (state, action: PayloadAction<billing.Claim[]>) => {
          state.claims.loading = false;
          state.claims.data = action.payload;
        },
      )
      .addCase(fetchClaims.rejected, (state, action) => {
        state.claims.loading = false;
        state.claims.error = action.error.message || "Failed to fetch claims";
      });
  },
});

export const { addBillingCode, updateBillingCode, deleteBillingCode } =
  billingSlice.actions;

export default billingSlice.reducer;
